import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * App-wide error boundary (Spec 118).
 *
 * Catches render-time errors in the routed page tree and renders a house
 * fallback card so the surrounding app shell (sidebar, header) stays intact.
 * Use a `key` tied to the current route so navigating away from a crashed
 * page resets the boundary.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="mx-auto max-w-2xl">
          <div
            className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
            data-testid="route-error-boundary"
          >
            <h2 className="text-sm font-semibold">Something went wrong on this page.</h2>
            <p className="mt-1 text-rose-700">
              An unexpected error occurred while rendering this view. You can try reloading the page.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="hub-btn hub-btn--ghost mt-3"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
